import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, effect } from "@angular/core";
import { BpOption } from "@models/ui-components/bp-option";
import { BehaviorSubject, Observable } from "rxjs";
import { skip, tap } from "rxjs/operators";
import {
	WatchlistWidgetFacade,
	WatchlistWidgetViewModel
} from "@modules/watchlist/domain/+state/watchlist-widget/watchlist-widget.facade";
import {
	BUY_ORDERS_PILL_VALUE,
	FOLLOWING_PILL_OPTIONS,
	FollowingPillsEnum,
	SELL_ORDERS_PILL_VALUE,
	TOTAL_ORDERS_PILL_VALUE,
	WATCHLIST_PILL_VALUE
} from "@helpers/const/following-options";
import { OrderType } from "@gql/offer";
import { IWidgetAdpOrder, OrdersDataModel } from "@modules/limit-orders/interfaces/limit-orders-widget-adp.model";
import { UntilDestroy } from "@ngneat/until-destroy";
import { getPills } from "@modules/limit-orders/utils/limit-orders-util";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { TradeTrackingName } from "@modules/checkout/entities/trade.rudderstack.tracking.model";
import { OrdersFollowingWidgetFacade } from "@modules/limit-orders/services/orders-widgets-following.facade";
import { OrderListReloadState } from "@modules/limit-orders/+state/limit-order-list-reload.state";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
	IFollowingCombinedFF,
	CombinedFollowingWidgetOrderData,
	InputData,
	IFollowingCountData,
	IFFinCount
} from "@modules/limit-orders/interfaces/limit-order-following.model";

export interface IFollowingWidget {
	showWatchlist: boolean;
	watchlistVM: WatchlistWidgetViewModel;
	selectedOrder: OrdersDataModel;
	isBuyLimitEnabled: boolean;
	isSellLimitEnabled: boolean;
	pills: pillsLimitOrderWidget;
	isLimOrdEnabled: boolean;
	isNewSellVisible: boolean;
	isCheckAllVisible: boolean;
	unverifiedOrUnconfirmed?: boolean;
	totalOrdersEnabled?: boolean;
}

interface pillsLimitOrderWidget {
	allPills: BpOption[];
	selectedPill: BpOption;
}

@UntilDestroy()
@Component({
	selector: "following-widget",
	templateUrl: "./following-widget.component.html",
	styleUrls: ["./following-widget.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [WatchlistWidgetFacade],
	standalone: false
})
export class FollowingWidgetComponent implements OnInit {
	private readonly watchlistWidgetFacade = inject(WatchlistWidgetFacade);
	private readonly limitOrdersTrackingFacade = inject(LimitOrdersTrackingFacade);
	private readonly followingWidgetFacade = inject(OrdersFollowingWidgetFacade);
	private readonly reloadState = inject(OrderListReloadState);
	private readonly destroyRef = inject(DestroyRef);

	//all aysnc data
	watchlistVM$ = this.watchListVMHelper();
	counts$ = this.followingWidgetFacade.ordersCountSignal$;
	limitOrderCombinedFF$ = this.followingWidgetFacade.limitOrdersCombinationFF$;

	allPills: BpOption[] = [];

	FOLLOWING_PILLS = FollowingPillsEnum;
	orderType = OrderType;

	public constructor() {
		this.watchlistWidgetFacade.initChartUpdates();

		// Effect to update pills from updated counts signal
		effect(() => {
			this.updatePillsCounts(this.counts$());
		});
	}

	ngOnInit(): void {
		this.reloadState
			.getOrderListReloadState$()
			.pipe(takeUntilDestroyed(this.destroyRef), skip(1))
			.subscribe((state) => {
				if (state.setReload) {
					this.onInvalidateOrdersCache();
				}
			});
	}

	private selectedPill$$ = new BehaviorSubject<BpOption>(FOLLOWING_PILL_OPTIONS[0]);
	public readonly selectedPill$ = this.selectedPill$$.asObservable();

	/**
	 * populateOrderPillsHelper
	 * @returns { orderBpList: BpOption[]; ff: IFollowingCombinedFF }
	 */
	private populateOrderPillsHelper(): { orderBpList: BpOption[]; ff: IFollowingCombinedFF } {
		const { data, ff } = this.counts$();
		const buyOrderPill = {
			...FOLLOWING_PILL_OPTIONS.find((pill) => pill.value === BUY_ORDERS_PILL_VALUE),
			amount: data.buyOrders
		};
		const sellOrderPill = {
			...FOLLOWING_PILL_OPTIONS.find((pill) => pill.value === SELL_ORDERS_PILL_VALUE),
			amount: data.sellOrders
		};
		const totalOrdersPill = {
			...FOLLOWING_PILL_OPTIONS.find((pill) => pill.value === TOTAL_ORDERS_PILL_VALUE),
			amount: data.buyOrders + data.sellOrders
		};

		return { orderBpList: [buyOrderPill, sellOrderPill, totalOrdersPill], ff };
	}

	/**
	 * getOrders$
	 * @param { OrderType } type
	 * @returns { Observable<CombinedFollowingWidgetOrderData<IWidgetAdpOrder[]>> }
	 */
	orders$(type: OrderType = null): Observable<CombinedFollowingWidgetOrderData<IWidgetAdpOrder[]>> {
		return this.followingWidgetFacade.getOrders$(type);
	}

	onFilterChange(pillOption: BpOption): void {
		if (pillOption.value !== WATCHLIST_PILL_VALUE) {
			this.limitOrdersTrackingFacade.widgetPillTapped(
				TradeTrackingName.LimitOrderWidgetPillTapped,
				pillOption.label
			);
		}
		this.selectedPill$$.next(FOLLOWING_PILL_OPTIONS.find((pill) => pill.value === pillOption.value));
	}

	onInvalidateOrdersCache(): void {
		this.followingWidgetFacade.invalidateAllCache();
	}

	goToLimitOrders(selectedPill: string): void {
		this.followingWidgetFacade.goToLimitOrdersList(null, selectedPill);
	}

	watchListVMHelper(): Observable<WatchlistWidgetViewModel> {
		return this.watchlistWidgetFacade.getViewModel$().pipe(
			tap((wl) => {
				const { ff, orderBpList } = this.populateOrderPillsHelper();

				const initialList = [
					{
						...FOLLOWING_PILL_OPTIONS.find((option) => option.value === WATCHLIST_PILL_VALUE),
						amount: wl.totalLength
					},
					...orderBpList
				];

				this.allPills = getPills(
					initialList,
					ff.sellEnabled,
					ff.buyEnabled,
					true, // On the following Widget the pill shouldn't be hidden as per ADP (Sell Power)
					ff.totalEnabled
				);
			})
		);
	}

	updatePillsCounts(counts: InputData<IFollowingCountData> & IFFinCount): void {
		const {
			data: { buyOrders, sellOrders, totalOrders }
		} = counts;

		this.allPills = this.allPills.map((pill) => {
			switch (pill.value) {
				case BUY_ORDERS_PILL_VALUE:
					pill.amount = buyOrders;
					break;
				case SELL_ORDERS_PILL_VALUE:
					pill.amount = sellOrders;
					break;
				case TOTAL_ORDERS_PILL_VALUE:
					pill.amount = totalOrders;
					break;
				default:
					break;
			}
			return pill;
		});
	}
}
